export const FOUNDATION_DESCRIPTION =
  'Get tested from the comfort of your home to get a complete picture of your baseline health';

export const CORE_DESCRIPTION =
  'Combine the comfort of at-home testing with in-centre evaluations for an in depth view of your health';

export const LIMITLESS_DESCRIPTION =
  'Experience in-depth multiomics testing and body composition analysis along with consistent health tracking to monitor and assist you at every step of your health journey';

export const ADDON_COPY = {
  METABOLOMICS: 'Understand how your body processes food',
  GUT_MICROBIOME: 'Analyze your gut bacteria and digestive health',
  GENOME_360: 'Complete genetic analysis for personalized health',
  HEAVY_METAL: 'Check for toxic metals that impact your long-term health',
  BODY_COMPOSITION: 'Measure body fat, bone density, and muscle precisely',
  METABOLIC_RISK: 'Spot early signs of metabolic imbalance',
  VO2_MAX: 'Measures oxygen uptake, fitness, endurance, efficiency',
  LIPOMICS: 'Advanced lipid profiling for cardiovascular health',
  EPIGENETIC: 'Discover your biological age and cellular health markers',
  RMR: 'Evaluates resting energy burn to understand your foundational metabolic rate',
  SIBO: 'Detect small intestinal bacterial overgrowth affecting digestion',
} as const;

// Thrive Program Constants
// This file centralizes all Thrive program name variations for easy maintenance

export const THRIVE_PROGRAMS = [
  'THRIVE',
  'Thrive',
  'thrive',
  'THRIVE EARLY',
  'Thrive Early',
  'thrive early',
  'THRIVE EARLY',
  'Thrive Early',
  'thrive early',
  // Backward compatibility: KICKSTART and BASE are treated as Thrive Early
  'KICKSTART',
  'Kickstart',
  'kickstart',
  'BASE',
  'Base',
  'base',
  'THRIVE CORE',
  'Thrive Core',
  'thrive core',
  'THRIVE FOUNDATION',
  'Thrive Foundation',
  'thrive foundation',
  'THRIVE LIMITLESS',
  'Thrive Limitless',
  'thrive limitless',
  'CONTINUUM',
  'Continuum',
  'continuum',
] as const;

// Type for Thrive program names
export type ThriveProgramName = (typeof THRIVE_PROGRAMS)[number];

// Helper function to check if a program name is a Thrive program
export const isThriveProgram = (programName: string): boolean => {
  return THRIVE_PROGRAMS.includes(programName as ThriveProgramName);
};

// Helper function to get normalized program name (for consistent comparisons)
export const getNormalizedProgramName = (programName: string): string => {
  return programName.toLowerCase().trim();
};

// Specific program type checks
export const isThriveEarly = (programName: string): boolean => {
  const normalized = getNormalizedProgramName(programName);
  return normalized === 'thrive early' || normalized === 'thrive early';
};

// TODO: include THRIVE CORE here once Core members are supported in the mobile app.
const APP_FIRST_PROGRAM_PATTERN = /early/i;
const IN_CENTER_PATTERN = /in[\s-]?center/i;

/** EARLY programs whose members onboard in the mobile app. */
export const isAppFirstProgram = (programName: string): boolean =>
  APP_FIRST_PROGRAM_PATTERN.test(programName) && !IN_CENTER_PATTERN.test(programName);

export const isThriveFoundation = (programName: string): boolean => {
  return getNormalizedProgramName(programName) === 'thrive foundation';
};

export const isContinuum = (programName: string): boolean => {
  return getNormalizedProgramName(programName) === 'continuum';
};

export const isThriveCore = (programName: string): boolean => {
  return getNormalizedProgramName(programName) === 'thrive core';
};

export const isThriveLimitless = (programName: string): boolean => {
  return getNormalizedProgramName(programName) === 'thrive limitless';
};

export const isThrive = (programName: string): boolean => {
  return getNormalizedProgramName(programName) === 'thrive';
};

// Testing/Development flags
export const BYPASS_THREE_DAY_CHECK_FOR_IN_CLINIC_STEP = false;
